<template>
  <modal
    title="Create Custom Index"
    size="full"
    :open="open"
    @close="closeModal"
  >
    <section v-show="display === 'intro'" class="intro">
      <h4>Choose an option</h4>
      <p>
        We have two ways of building out a custom index. You can either define a subset of cards from our Ladder
        database to analyze or search individual cards manually to build your index.
      </p>

      <div class="cta-options">
        <div>
          <cta-card
            icon="construction"
            title="Define Subset"
            description="Choose from multiple parameters to build a subset of cards from our Ladder database"
            @click="display = 'subset'"
          />
        </div>

        <div>
          <cta-card
            icon="search"
            title="Search Cards"
            description="Add cards to your index manually one at a time by searching our Ladder database"
            @click="display = 'search'"
          />
        </div>
      </div>
    </section>

    <section class="form">
      <text-input
        v-show="display !== 'intro'"
        v-model.trim="label"
        label="Index Title"
        maxlength="24"
      />
    </section>

    <section v-show="display === 'search'" class="form">
      <p>
        Search our Ladder database and select the cards you want in your index. You must select at least 5 cards with a
        max of {{ searchLimit }} cards.
      </p>

      <div v-show="cardIds.length < searchLimit">
        <card-search
          placeholder="Search for a card"
          keep-open-on-select
          :exclude-ids="cardIds"
          @select="addCard"
        />

        <br />
      </div>

      <div v-for="card in cards" :key="card.id" class="card-item">
        <align no-gap>
          <icon-button icon="close" @click="removeCard(card.id)" />

          <card-list-item :card="card" :fields="['currentValue']" />
        </align>
      </div>
    </section>

    <section v-show="display === 'subset'" class="form">
      <p>
        Setup parameters for which cards will be included in your index. Each filter you apply will reduce the number
        of cards in your index from the Ladder. The number of cards in the index must be less than {{ subsetLimit }}.
      </p>

      <select-input
        v-model="filters.category"
        label="Category"
        searchable
        :options="categoryOptions"
      />

      <multiselect v-model="filters.conditions" label="Conditions" :options="conditionOptions" />

      <text-input
        v-model.trim="filters.player"
        label="Player"
        maxlength="64"
        :options="playerOptions"
        :loading="loadingPlayers"
        @input="loadPlayers"
      />

      <div class="input-group">
        <currency-input v-model="filters.minValue" label="Min Value" />

        <currency-input v-model="filters.maxValue" label="Max Value" />
      </div>

      <div class="input-group">
        <select-input v-model="filters.minYear" label="Min Year" :options="yearOptions" />

        <select-input v-model="filters.maxYear" label="Max Year" :options="yearOptions" />
      </div>

      <div class="input-group">
        <text-input v-model.trim="filters.minPop" type="number" label="Min Pop" />

        <text-input v-model.trim="filters.maxPop" type="number" label="Max Pop" />
      </div>

      <p v-show="cardCountString">
        {{ cardCountString }} cards in this index
      </p>
      <spinner v-show="loadingCardCount" inline />
    </section>

    <template #footer>
      <align v-show="display !== 'intro'" justify="space-between">
        <div class="button-row">
          <btn
            :loading="submitting"
            :disabled="disabled"
            loading-text="Submitting"
            @click="submit"
          >
            Submit
          </btn>
        </div>

        <btn kind="secondary" @click="closeModal">
          Cancel
        </btn>
      </align>
    </template>
  </modal>
</template>

<script>
import { mapState } from 'vuex';
import clone from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import * as Sentry from '@sentry/vue';

import { DEFAULT_CUSTOM_INDEX_FILTERS, CONDITION_OPTIONS, CATEGORY_OPTIONS } from '@cardladder/shared/constants';
import { searchService } from '@/services';

import CardSearch from '@/cards/CardSearch';
import CardListItem from '@cardladder/shared/components/CardListItem';
import { authService } from '../services';
import { getYearOptions } from '@cardladder/shared/utils/format.util';

const SUCCESS_MESSAGE = 'Creating your index, it takes a minute. Refresh this page and you will see it.';

export default {
  components: {
    CardSearch,
    CardListItem,
  },

  props: {
    open: Boolean,
  },

  data() {
    return {
      display: 'intro',
      label: '',
      filters: clone(DEFAULT_CUSTOM_INDEX_FILTERS),
      playerList: [],
      cards: [],
      loadingPlayers: false,
      submitting: false,
      categoryOptions: CATEGORY_OPTIONS,
      conditionOptions: CONDITION_OPTIONS,
      cardCount: null,
      loadingCardCount: false,
      subsetLimit: 1000,
      searchLimit: 100,
    };
  },

  mounted() {
    this.loadPlayers = debounce(this.loadPlayers, 1000);
    this.checkCardCount = debounce(this.checkCardCount, 1000);
  },

  watch: {
    filters: {
      handler() {
        this.checkCardCount();
      },
      deep: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    disabled() {
      if (!this.label || this.loadingCardCount) {
        return true;
      }

      if (this.display === 'search') {
        return this.cards.length < 5 || this.cards.length > this.searchLimit;
      }

      if (this.display === 'subset') {
        return !this.cardCount || this.cardCount >= this.subsetLimit;
      }

      return false;
    },

    cardIds() {
      return this.cards.map(({ id }) => id);
    },

    isFiltering() {
      return this.display === 'subset' && !isEqual(this.filters, DEFAULT_CUSTOM_INDEX_FILTERS);
    },

    yearOptions() {
      return getYearOptions(100);
    },

    playerOptions() {
      return this.playerList.map(({ id, name }) => ({ value: id, label: name }));
    },

    cardCountString() {
      if (this.cardCount === null || this.loadingCardCount) {
        return null;
      }

      return this.cardCount === this.subsetLimit
        ? `> ${ this.$filters.number(this.subsetLimit)}`
        : this.$filters.number(this.cardCount);
    },
  },

  methods: {
    submit() {
      if (this.display === 'search') {
        this.submitSearch();
      } else {
        this.submitSubset();
      }
    },

    async submitSubset() {
      this.submitting = true;

      try {
        await authService.createCustomIndex(this.user.uid, {
          type: 'subset',
          label: this.label,
          filters: this.filters,
        });

        this.$toasted.success(SUCCESS_MESSAGE);
        this.$emit('updated');
        this.closeModal();
      } catch (e) {
        this.$toasted.error('Error creating your index');
        Sentry.captureException(e);
      }

      this.submitting = false;
    },

    async submitSearch() {
      this.submitting = true;

      try {
        await authService.createCustomIndex(this.user.uid, {
          type: 'search',
          label: this.label,
          cardIds: this.cardIds,
        });

        this.$toasted.success(SUCCESS_MESSAGE);
        this.$emit('updated');
        this.closeModal();
      } catch (e) {
        this.$toasted.error('Error creating your index');
        Sentry.captureException(e);
      }

      this.submitting = false;
    },

    async loadPlayers() {
      this.loadingPlayers = true;

      try {
        const data = await searchService.search({
          index: 'playerlist',
          query: this.filters.player || '',
        });
        this.playerList = data.hits;
      } catch (e) {
        // swallow
      }

      this.loadingPlayers = false;
    },

    async addCard(card) {
      this.cards.push(card);
    },

    removeCard(cardId) {
      this.cards = this.cards.filter(({ id }) => id !== cardId);
    },

    async checkCardCount() {
      if (!this.isFiltering) {
        return;
      }

      this.loadingCardCount = true;

      try {
        const data = await searchService.search({
          index: 'cards',
          limit: this.subsetLimit,
          filters: this.filters,
        });

        this.cardCount = data.hits.length;
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loadingCardCount = false;
    },

    reset() {
      this.filters = clone(DEFAULT_CUSTOM_INDEX_FILTERS);
      this.display = 'intro';
      this.cardCount = null;
      this.label = '';
    },

    closeModal() {
      this.reset();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.intro {
  text-align: center;

  p {
    max-width: 600px;
    margin: 0 auto;
  }
}

h4 {
  margin-bottom: $gp * .5;
}

.cta-options {
  display: grid;
  gap: $gp;
  margin: $gp * 2 auto;
  grid-template-columns: repeat(2, 1fr);
  max-width: 800px;

  @media($small-down) {
    grid-template-columns: repeat(1, 1fr);
  }

  > div {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.form {
  max-width: 600px;
  margin: $gp auto;
}
</style>
